import { HtmlFormField } from '@/presentation/components/scholarship-form/html-editor/html-form-field';

import {
	Degree,
	StudyExperience,
	ApplicationBasis,
	ScholarshipFormat,
	Gender,
	Race,
	DeadlineType,
	Presence,
	ScholarshipType,
} from '@studyportals/sp-scholarships-api-interface';

import { ref } from 'vue';
import { Scholarship } from '@/presenters/scholarship';
import { MassSelectFormField } from './mass-select/mass-select-form-field';
import { DeadlineFormField } from './deadline-editor/deadline-form-field';
import { Clearable } from './type-extensions/clearable';
import { ProviderFormField } from './provider-editor/provider-form-field';

export default class ScholarshipFormModel {
	public sourceUrl = ref<string>();
	public foundAtUrl = ref<string>();
	public destinationCountries = new MassSelectFormField();
	public destinationStates = new MassSelectFormField();
	public originCountries = new MassSelectFormField();
	public originStates = new MassSelectFormField();
	public disciplines = new MassSelectFormField();
	public grantDescription = ref<string>();
	public grantAmount = ref<number>();
	public grantCurrencyIso = new Clearable<string>();
	public deadline = new DeadlineFormField();
	public degrees = ref<Degree[]>([]);
	public title = ref<string>();
	public description = new HtmlFormField();
	public eligibility = new HtmlFormField();
	public benefits = new HtmlFormField();
	public applicationInfo = new HtmlFormField();
	public applicationFormUrl = ref<string>();
	public provider = new ProviderFormField();
	public minimumExperience = new Clearable<StudyExperience>();
	public isAwardedMultipleTimes = ref<boolean>();
	public applicationBasis = ref<ApplicationBasis[]>([]);
	public formats = ref<ScholarshipFormat[]>([]);
	public types = ref<ScholarshipType[]>([]);
	public ageMin = ref<number>();
	public ageMax = ref<number>();
	public gender = new Clearable<Gender>();
	public race = new Clearable<Race>();
	public summary = new HtmlFormField();
	public isPublished = ref<boolean>(true);

	public static fromScholarship(scholarship: Scholarship): ScholarshipFormModel {
		const model = new ScholarshipFormModel();

		model.sourceUrl.value = scholarship.sourceUrl;
		model.foundAtUrl.value = scholarship.foundAtUrl ?? undefined;
		model.destinationCountries.specific.keys.value = [...scholarship.destinationCountries.map((c) => c.id)];
		model.destinationCountries.allSelected.value = scholarship.allDestinationCountries;
		model.destinationStates.specific.keys.value = [...scholarship.destinationStates.map((s) => s.id)];
		model.destinationStates.allSelected.value = scholarship.allDestinationStates;
		model.originCountries.specific.keys.value = [...scholarship.originCountries.map((c) => c.id)];
		model.originCountries.allSelected.value = scholarship.allOriginCountries;
		model.originStates.specific.keys.value = [...scholarship.originStates.map((s) => s.id)];
		model.originStates.allSelected.value = scholarship.allOriginStates;
		model.disciplines.specific.keys.value = [...scholarship.disciplines.map((d) => d.id)];
		model.disciplines.allSelected.value = scholarship.allDisciplines;
		model.grantDescription.value = scholarship.grantDescription;
		model.grantAmount.value = scholarship.grantAmount ?? undefined;
		model.grantCurrencyIso.inputValue.value = scholarship.grantCurrencyIso ?? undefined;
		model.deadline.isAnytime.value = scholarship.deadlineType === DeadlineType.ANY_TIME;
		model.deadline.isNotSpecified.value = scholarship.deadlineType === DeadlineType.NOT_SPECIFIED;
		model.deadline.isSpecified.value = scholarship.deadlineType === DeadlineType.SPECIFIED;
		model.deadline.date.value = scholarship.deadline?.toShortDateString() ?? undefined;
		model.degrees.value = [...scholarship.degrees];
		model.title.value = scholarship.title;
		model.description.content.value = scholarship.description;
		model.eligibility.content.value = scholarship.eligibility;
		model.benefits.content.value = scholarship.benefits;
		model.applicationInfo.content.value = scholarship.applicationInfo ?? '';
		model.applicationFormUrl.value = scholarship.applicationFormUrl ?? undefined;
		model.minimumExperience.inputValue.value = scholarship.minimumExperience ?? undefined;
		model.isAwardedMultipleTimes.value = scholarship.isAwardedMultipleTimes;
		model.applicationBasis.value = [...scholarship.applicationBasis];
		model.formats.value = [...scholarship.formats];
		model.types.value = [...scholarship.types];
		model.ageMin.value = scholarship.ageMin ?? undefined;
		model.ageMax.value = scholarship.ageMax ?? undefined;
		model.gender.inputValue.value = scholarship.gender ?? undefined;
		model.race.inputValue.value = scholarship.race.length > 0 ? scholarship.race[0] : undefined;
		model.summary.content.value = scholarship.summary;
		model.isPublished.value = scholarship.presence !== Presence.PRIVATE;

		model.provider.updateFrom(scholarship);

		return model;
	}
}
